// LICENSE : MIT
"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    }
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var Dispatcher_1 = require("./Dispatcher");
var DispatcherPayloadMeta_1 = require("./DispatcherPayloadMeta");
var UseCaseInstanceMap_1 = require("./UseCaseInstanceMap");
// payloads
var CompletedPayload_1 = require("./payload/CompletedPayload");
var DidExecutedPayload_1 = require("./payload/DidExecutedPayload");
var WillExecutedPayload_1 = require("./payload/WillExecutedPayload");
var WillNotExecutedPayload_1 = require("./payload/WillNotExecutedPayload");
var assert_1 = require("./util/assert");
var Events_1 = require("./Events");
/**
 * Create wrapper object of a UseCase.
 * This wrapper object only has `execute()` method.
 */
var proxifyUseCase = function (useCase, handlers) {
    var isExecuted = false;
    var execute = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (process.env.NODE_ENV !== "production") {
            if (isExecuted) {
                console.error("Warning(UseCase): " + useCase.name + "#execute was called more than once.");
            }
        }
        isExecuted = true;
        // should the useCase execute?
        if (typeof useCase.shouldExecute === "function") {
            var shouldExecute = useCase.shouldExecute(args);
            if (typeof shouldExecute !== "boolean") {
                var error = new Error("<" + useCase.name + ">#shouldExecute should return boolean value. Actual return value: " + shouldExecute);
                return {
                    type: "InvalidUsage",
                    error: error
                };
            }
            if (!shouldExecute) {
                handlers.onWillNotExecute(args);
                return {
                    type: "ShouldNotExecute"
                };
            }
        }
        // will execute
        handlers.onWillExecute(args);
        try {
            // execute
            var result = useCase.execute.apply(useCase, args);
            handlers.onDidExecute(result);
            return {
                type: "SuccessExecuteReturnValue",
                value: result
            };
        }
        catch (error) {
            handlers.onError(error);
            handlers.onDidExecute();
            return {
                type: "ErrorInExecute",
                error: error
            };
        }
    };
    // Add debug displayName
    if (process.env.NODE_ENV !== "production") {
        execute.displayName = "Wrapped<" + useCase.name + ">#execute";
    }
    return {
        execute: execute
    };
};
/**
 * When child is completed after parent did completed, display warning warning message
 * @private
 */
var warningUseCaseIsAlreadyReleased = function (parentUseCase, useCase, payload, meta) {
    console.error("Warning(UseCase): " + useCase.name + "'s parent UseCase(" + parentUseCase.name + ") is already released.\nThis UseCase(" + useCase.name + ") will not work correctly.\nhttps://almin.js.org/docs/warnings/usecase-is-already-released.html\n", payload, meta);
};
/**
 * `UseCaseExecutor` is a helper class for executing UseCase.
 *
 * You can not create the instance of UseCaseExecutor directory.
 * You can get the instance by `Context#useCase(useCase)`,
 *
 */
var UseCaseExecutorImpl = /** @class */ (function (_super) {
    __extends(UseCaseExecutorImpl, _super);
    /**
     * @param   useCase
     * @param   parent
     *      parent is parent of `useCase`
     * @param   dispatcher
     * @public
     *
     * **internal** documentation
     */
    function UseCaseExecutorImpl(_a) {
        var useCase = _a.useCase, parent = _a.parent;
        var _this = _super.call(this) || this;
        if (process.env.NODE_ENV !== "production") {
            // execute and finish =>
            var useCaseName = useCase.name;
            assert_1.assertOK(typeof useCaseName === "string", "UseCase instance should have constructor.name " + useCase);
            assert_1.assertOK(typeof useCase.execute === "function", "UseCase instance should have #execute function: " + useCaseName);
        }
        _this.useCase = useCase;
        _this._parentUseCase = parent;
        _this._releaseHandlers = [];
        /**
         * ## Delegating Payload
         *
         * UseCase -> UseCaseExecutor -> UnitOfWork -> Dispatcher
         *
         */
        var unListenUseCaseToDispatcherHandler = _this.useCase.pipe(_this);
        _this._releaseHandlers.push(unListenUseCaseToDispatcherHandler);
        // If this is parent UseCase, Parent UseCase -> UnitOfWork
        if (_this._parentUseCase) {
            // Child -> Parent
            var unListenUseCaseExecutorToDispatcherHandler = _this.pipe(_this._parentUseCase);
            _this._releaseHandlers.push(unListenUseCaseExecutorToDispatcherHandler);
        }
        _this.releaseEvents = new Events_1.Events();
        return _this;
    }
    UseCaseExecutorImpl.prototype.willNotExecuteUseCase = function (args) {
        var payload = new WillNotExecutedPayload_1.WillNotExecutedPayload({
            args: args
        });
        var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
            useCase: this.useCase,
            parentUseCase: this._parentUseCase,
            isTrusted: true,
            isUseCaseFinished: true
        });
        this.dispatch(payload, meta);
    };
    /**
     * @param   [args] arguments of the UseCase
     */
    UseCaseExecutorImpl.prototype.willExecuteUseCase = function (args) {
        // Add instance to manager
        // It should be removed when it will be completed.
        UseCaseInstanceMap_1.UseCaseInstanceMap.set(this.useCase, this);
        var payload = new WillExecutedPayload_1.WillExecutedPayload({
            args: args
        });
        var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
            useCase: this.useCase,
            parentUseCase: this._parentUseCase,
            isTrusted: true,
            isUseCaseFinished: false
        });
        this.dispatch(payload, meta);
        // Warning: parentUseCase is already released
        if (process.env.NODE_ENV !== "production") {
            if (this._parentUseCase && !UseCaseInstanceMap_1.UseCaseInstanceMap.has(this._parentUseCase)) {
                warningUseCaseIsAlreadyReleased(this._parentUseCase, this.useCase, payload, meta);
            }
        }
    };
    /**
     * dispatch did execute each UseCase
     */
    UseCaseExecutorImpl.prototype.didExecuteUseCase = function (value) {
        // if the UseCase return a promise, almin recognize the UseCase as continuous.
        // In other word, If the UseCase want to continue, please return a promise object.
        var isResultPromise = typeof value === "object" && value !== null && typeof value.then == "function";
        var isUseCaseFinished = !isResultPromise;
        var payload = new DidExecutedPayload_1.DidExecutedPayload({
            value: value
        });
        var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
            useCase: this.useCase,
            parentUseCase: this._parentUseCase,
            isTrusted: true,
            isUseCaseFinished: isUseCaseFinished
        });
        this.dispatch(payload, meta);
    };
    /**
     * dispatch complete each UseCase
     * @param   [value] unwrapped result value of the useCase executed
     */
    UseCaseExecutorImpl.prototype.completeUseCase = function (value) {
        var payload = new CompletedPayload_1.CompletedPayload({
            value: value
        });
        var meta = new DispatcherPayloadMeta_1.DispatcherPayloadMetaImpl({
            useCase: this.useCase,
            parentUseCase: this._parentUseCase,
            isTrusted: true,
            isUseCaseFinished: true
        });
        this.dispatch(payload, meta);
        // Warning: parentUseCase is already released
        if (process.env.NODE_ENV !== "production") {
            if (this._parentUseCase && !UseCaseInstanceMap_1.UseCaseInstanceMap.has(this._parentUseCase)) {
                warningUseCaseIsAlreadyReleased(this._parentUseCase, this.useCase, payload, meta);
            }
        }
        // Delete the reference from instance manager
        // It prevent leaking of instance.
        UseCaseInstanceMap_1.UseCaseInstanceMap.delete(this.useCase);
    };
    /**
     * Call handler when this UseCaseExecutor will be released
     * @param handler
     */
    UseCaseExecutorImpl.prototype.onReleaseOnce = function (handler) {
        this.releaseEvents.addEventListenerOnce(handler);
    };
    /**
     * **Stability**: Deprecated(Previously: experimental)
     *
     * - This feature is subject to change. It may change or be removed in future versions.
     * - If you inserting in this, please see <https://github.com/almin/almin/issues/193>
     *
     * Similar to `execute(arguments)`, but it accept an executor function insteadof `arguments`
     * `executor(useCase => useCase.execute())` return a Promise object that resolved with undefined.
     *
     * This method is type-safe. It is useful for TypeScript.
     *
     * ## Example
     *
     * ```js
     * context.useCase(new MyUseCase())
     *  .executor(useCase => useCase.execute("value"))
     *  .then(() => {
     *    console.log("test");
     *  });
     * ```
     *
     * ## Notes
     *
     * ### What is difference between `executor(executor)` and `execute(arguments)`?
     *
     * The `execute(arguments)` is a alias of following codes:
     *
     * ```js
     * context.useCase(new MyUseCase())
     *  .execute("value")
     * // ===
     * context.useCase(new MyUseCase())
     *  .executor(useCase => useCase.execute("value"))
     * ```
     *
     * ### Why executor's result always to be undefined?
     *
     * `execute()` return a Promise that will resolve `undefined` by design.
     * In CQRS, the command always have returned void type.
     *
     * - http://cqrs.nu/Faq
     *
     * So, `execute()` only return command result that is success or failure.
     * You should not relay on the result value of the useCase#execute.
     *
     * @deprecated Use `execute()` instead of `executor()`
     * Almin 0.18+ make `execute` type complete.
     * It will be remove in the future.
     *
     * Please apply migration scripts:
     * https://github.com/almin/almin/issues/356
     *
     */
    UseCaseExecutorImpl.prototype.executor = function (executor) {
        var _this = this;
        // TODO: executor() is duplication function of execute()
        // It will be removed in the future.
        // Show deprecated waring
        console.warn("Warning(UseCase): `executor(useCase => useCase.execute(args))` is deprecated. \n        \nUse `execute(args)` insteadof it.        \nSee https://github.com/almin/almin/issues/356");
        if (typeof executor !== "function") {
            console.error("Warning(UseCase): executor argument should be function. But this argument is not function: ", executor);
            return Promise.reject(new Error("executor(fn) arguments should be function"));
        }
        var proxyfiedUseCase = proxifyUseCase(this.useCase, {
            onWillNotExecute: function (args) {
                _this.willNotExecuteUseCase(args);
            },
            onWillExecute: function (args) {
                _this.willExecuteUseCase(args);
            },
            onDidExecute: function (result) {
                _this.didExecuteUseCase(result);
            },
            onError: function (error) {
                _this.useCase.throwError(error);
            }
        });
        var executorResult = executor(proxyfiedUseCase);
        var executedResult = executorResult !== undefined
            ? executorResult
            : {
                type: "SuccessExecuteNoReturnValue"
            };
        if (executedResult.type === "ShouldNotExecute") {
            this.release();
            return Promise.resolve();
        }
        var startingExecutor = new Promise(function (resolve, reject) {
            switch (executedResult.type) {
                case "InvalidUsage":
                    return reject(executedResult.error);
                case "ErrorInExecute":
                    return reject(executedResult.error);
                case "SuccessExecuteReturnValue": {
                    return Promise.resolve(executedResult.value).then(resolve, function (error) {
                        _this.useCase.throwError(error);
                        return reject(error);
                    });
                }
                case "SuccessExecuteNoReturnValue":
                    return resolve();
            }
        });
        return startingExecutor
            .then(function (result) {
            _this.completeUseCase(result);
            _this.release();
        })
            .catch(function (error) {
            _this.completeUseCase();
            _this.release();
            return Promise.reject(error);
        });
    };
    /**
     * Execute UseCase instance.
     * UseCase is a executable object that has `execute` method.
     *
     * This method invoke UseCase's `execute` method and return a promise<void>.
     * The promise will be resolved when the UseCase is completed finished.
     *
     * ### Why executor's result always to be undefined?
     *
     * `execute()` return a Promise that will resolve `undefined` by design.
     * In CQRS, the command always have returned void type.
     *
     * - http://cqrs.nu/Faq
     *
     * So, `execute()` only return command result that is success or failure.
     * You should not relay on the result value of the useCase#execute.
     *
     * ## Notes
     *
     * > Added: Almin 0.17.0+
     *
     * `execute()` support type check in Almin 0.17.0.
     * However, it has a limitation about argument lengths.
     * For more details, please see <https://github.com/almin/almin/issues/107#issuecomment-384993458>
     *
     * > Added: Almin 0.18.0+
     *
     * `execute()` support type check completely.
     * No more need to use `executor()` for typing.
     *
     */
    UseCaseExecutorImpl.prototype.execute = function () {
        var _this = this;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        // Notes: proxyfiedUseCase has not timeout
        // proxiedUseCase will resolve by UseCaseWrapper#execute
        // For more details, see <UseCaseLifeCycle-test.ts>
        var proxyfiedUseCase = proxifyUseCase(this.useCase, {
            onWillNotExecute: function (args) {
                _this.willNotExecuteUseCase(args);
            },
            onWillExecute: function (args) {
                _this.willExecuteUseCase(args);
            },
            onDidExecute: function (result) {
                _this.didExecuteUseCase(result);
            },
            onError: function (error) {
                _this.useCase.throwError(error);
            }
        });
        // Note: almin disallow to call `executor(useCase => { setTimeout(() => useCase.execute(), 0}})` asynchronously
        // You should call UseCase#execute synchronously.
        var executorResult = proxyfiedUseCase.execute.apply(proxyfiedUseCase, args);
        // `executorResult` is undefined means that the UseCase#execute never return any value
        // In JavaScript, no return as undefined value
        var executedResult = executorResult !== undefined
            ? executorResult
            : {
                type: "SuccessExecuteNoReturnValue"
            };
        // if does not execute, release and resolve as soon as possible
        if (executedResult.type === "ShouldNotExecute") {
            this.release();
            return Promise.resolve();
        }
        var startingExecutor = new Promise(function (resolve, reject) {
            switch (executedResult.type) {
                case "InvalidUsage":
                    return reject(executedResult.error);
                case "ErrorInExecute":
                    return reject(executedResult.error);
                case "SuccessExecuteReturnValue": {
                    // try to resolve return value
                    // if the return promise is reject, report error from UseCase
                    return Promise.resolve(executedResult.value).then(resolve, function (error) {
                        _this.useCase.throwError(error);
                        return reject(error);
                    });
                }
                case "SuccessExecuteNoReturnValue":
                    // The UseCase#execute just success without return value
                    return resolve();
            }
        });
        return startingExecutor
            .then(function (result) {
            _this.completeUseCase(result);
            _this.release();
        })
            .catch(function (error) {
            _this.completeUseCase();
            _this.release();
            return Promise.reject(error);
        });
    };
    /**
     * release all events handler.
     * You can call this when no more call event handler
     */
    UseCaseExecutorImpl.prototype.release = function () {
        this._releaseHandlers.forEach(function (releaseHandler) { return releaseHandler(); });
        this._releaseHandlers.length = 0;
        this.releaseEvents.emit(undefined);
    };
    return UseCaseExecutorImpl;
}(Dispatcher_1.Dispatcher));
exports.UseCaseExecutorImpl = UseCaseExecutorImpl;
//# sourceMappingURL=UseCaseExecutor.js.map


//////////////////
// WEBPACK FOOTER
// /home/travis/build/proofdict/proofdict/node_modules/almin/lib/src/UseCaseExecutor.js
// module id = 474
// module chunks = 0